import {
  HomeOutlined,
  SunOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  MoonOutlined,
} from "@ant-design/icons";
import { SERVICE_COLORS } from "./features/sitter/components/services/services.config";

export const Roles = {
  Admin: "Admin",
  Sitter: "Sitter",
  Client: "Client",
};

export enum ProfileTypeEnum {
  ADMIN = "admin",
  SITTER = "sitter",
  CLIENT = "client",
}

// type: "central" | "east" | "west" | "north" | "northeast"
export const SG_TOWNS_WITH_COORDS = [
  { name: "Ang Mo Kio", lat: 1.3691, lng: 103.8454, type: "north-east" },
  { name: "Bedok", lat: 1.3236, lng: 103.9273, type: "east" },
  { name: "Bishan", lat: 1.3526, lng: 103.8355, type: "central" },
  { name: "Bukit Batok", lat: 1.3483, lng: 103.7499, type: "west" },
  { name: "Bukit Merah", lat: 1.2776, lng: 103.819, type: "central" },
  { name: "Bukit Panjang", lat: 1.3786, lng: 103.7643, type: "west" },
  { name: "Bukit Timah", lat: 1.3294, lng: 103.8021, type: "central" },
  { name: "Central Area (CBD)", lat: 1.2855, lng: 103.852, type: "central" },
  { name: "Choa Chu Kang", lat: 1.385, lng: 103.7455, type: "west" },
  { name: "Clementi", lat: 1.3151, lng: 103.7644, type: "west" },
  { name: "Geylang", lat: 1.3185, lng: 103.883, type: "central" },
  { name: "Hougang", lat: 1.3611, lng: 103.8908, type: "north-east" },
  { name: "Jurong East", lat: 1.3342, lng: 103.742, type: "west" },
  { name: "Jurong West", lat: 1.3406, lng: 103.709, type: "west" },
  { name: "Kallang", lat: 1.307, lng: 103.866, type: "central" },
  { name: "Lim Chu Kang", lat: 1.4315, lng: 103.7084, type: "north" },
  { name: "Mandai", lat: 1.418, lng: 103.811, type: "north" },
  { name: "Marine Parade", lat: 1.3038, lng: 103.9072, type: "east" },
  { name: "Novena", lat: 1.3244, lng: 103.8431, type: "central" },
  { name: "Orchard", lat: 1.3049, lng: 103.8318, type: "central" },
  { name: "Pasir Ris", lat: 1.3731, lng: 103.9495, type: "east" },
  { name: "Paya Lebar", lat: 1.318, lng: 103.8925, type: "east" },
  { name: "Punggol", lat: 1.4053, lng: 103.902, type: "north-east" },
  { name: "Queenstown", lat: 1.294, lng: 103.7873, type: "central" },
  { name: "Sembawang", lat: 1.4491, lng: 103.8185, type: "north" },
  { name: "Sengkang", lat: 1.3924, lng: 103.8953, type: "north-east" },
  { name: "Serangoon", lat: 1.3508, lng: 103.8735, type: "north-east" },
  { name: "Tampines", lat: 1.3496, lng: 103.9568, type: "east" },
  { name: "Tanah Merah", lat: 1.32, lng: 103.948, type: "east" },
  { name: "Tanjong Pagar", lat: 1.2763, lng: 103.8439, type: "central" },
  { name: "Toa Payoh", lat: 1.3342, lng: 103.8495, type: "central" },
  { name: "Woodlands", lat: 1.436, lng: 103.7865, type: "north" },
  { name: "Yishun", lat: 1.4294, lng: 103.835, type: "north" },
];

export const TOWNS = SG_TOWNS_WITH_COORDS.map((town) => ({
  label: town.name,
  value: town.name,
  type: town.type,
}));

export const MB_5 = 5 * 1024 * 1024;

export const EventEmitterMessageTypes = {
  COPE_IMAGE: "crop-image",
  OPEN_CHAT: "open-chat",
} as const;

export type EventEmitterMessageTypes =
  (typeof EventEmitterMessageTypes)[keyof typeof EventEmitterMessageTypes];

export const SERVICES = {
  PetBoarding: "Pet Boarding",
  DaytimePetCare: "Daytime Pet Care",
  PetWalking: "Pet Walking",
  DropInVisits: "Drop-In Visits",
  InHomePetSitting: "In-Home Pet Sitting",
};

export const LOCATION_TYPE = {
  SITTER_HOME: "At the sitter's home",
  OWNER_HOME: "At your home",
};

export const SORT = {
  RELEVANCE: "relevance",
  POPULARITY: "popularity",
  HIGHEST_FIRST: "highest_first",
  LOWEST_FIRST: "lowest_first",
  REVIEWS: "reviews",
  RATING: "rating",
};

export const SortDisplayValues: { [key: string]: string } = {
  relevance: "Relevance",
  popularity: "Popularity",
  highest_first: "Price: highest first",
  lowest_first: "Price: lowest first",
  reviews: "Number of reviews",
  rating: "Best rating",
};

export const SERVICES_DETAILS = [
  {
    key: "PetBoarding",
    title: "Pet Boarding",
    shortDescription: "Overnight stay at the sitter’s home.",
    description:
      "Cozy overnight care with full supervision at the sitter's home.",
    locationType: LOCATION_TYPE.SITTER_HOME,
    icon: HomeOutlined,
    theme: SERVICE_COLORS.dog_boarding,
  },

  {
    key: "DaytimePetCare",
    title: "Daytime Pet Care",
    shortDescription: "Daytime supervision and play.",
    description:
      "Your pet enjoys social play and comfort at the sitter’s home.",
    locationType: LOCATION_TYPE.SITTER_HOME,
    icon: SunOutlined,
    theme: SERVICE_COLORS.doggy_day_care,
  },

  {
    key: "PetWalking",
    title: "Pet Walking",
    shortDescription: "Healthy and refreshing walks.",
    description:
      "A paced, enjoyable walk including bathroom breaks and stimulation.",
    locationType: LOCATION_TYPE.OWNER_HOME,
    icon: EnvironmentOutlined,
    theme: SERVICE_COLORS.dog_walking,
  },

  {
    key: "DropInVisits",
    title: "Drop-In Visits",
    shortDescription: "Quick check-in at your home.",
    description: "Feed, refresh water, clean litter, and check on your pet.",
    locationType: LOCATION_TYPE.OWNER_HOME,
    icon: ClockCircleOutlined,
    theme: SERVICE_COLORS.home_visit,
  },

  {
    key: "InHomePetSitting",
    title: "In-Home Pet Sitting",
    shortDescription: "Overnight stay at your home.",
    description: "A sitter stays overnight in your home for full-time care.",
    locationType: LOCATION_TYPE.OWNER_HOME,
    icon: MoonOutlined,
    theme: SERVICE_COLORS.house_sitting,
  },
];

export const SITE_NAME = import.meta.env.VITE_SITE_NAME;
export const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL;

export const LANGUAGES = [
  { code: "en-SG", label: "English" },
  { code: "zh-SG", label: "中文 (Chinese)" },
  { code: "ms-SG", label: "Bahasa Melayu (Malay)" },
  { code: "ta-SG", label: "தமிழ் (Tamil)" },
];

export const WEEK_DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];


export const PETS = [
  { value: "dog", label: "Dog" },
  { value: "cat", label: "Cat" },
  { value: "rabbit", label: "Rabbit" },
  { value: "guinea_pig", label: "Guinea Pig" },
  { value: "hamster", label: "Hamster" },
  { value: "ferret", label: "Ferret" },
  { value: "bird", label: "Bird" },
  { value: "fish", label: "Fish" },
  { value: "reptile", label: "Reptile" },
  { value: "horse", label: "Horse" },
  { value: "farm_animal", label: "Farm Animal" },
  { value: "other", label: "Other" },
];